/**
 * Where a haulage job delivers to: a real town, and in it somewhere a truck
 * would unload — an industrial estate, a retail or business park — read from
 * the same OpenStreetMap vector tiles the world is built from.
 *
 * Asked once per job offered: the place labels in the tiles around the point
 * the job aims for, at a zoom that has the towns (or, for a short run, the
 * villages and suburbs too); then the land use in the detailed tiles round
 * the town chosen, for an estate to drive into. Without one there, the load
 * goes to the town's centre.
 */

import { VectorTile } from '@mapbox/vector-tile';
import { PbfReader } from 'pbf';
import { chooseDropOff, type Place, type Site } from '../core/economy/jobs.js';
import { latToTileYf, lonToTileXf, tileXToLon, tileYToLat } from '../core/geo/mercator.js';

/** The place kinds a load goes to, as the tiles name them, and as the job reads them. */
const PLACE_KIND: Record<string, string> = {
  capital: 'city', state_capital: 'city', city: 'city', town: 'town', village: 'village', suburb: 'suburb',
};
/** Land a truck unloads on. */
const SITE_KINDS = new Set(['industrial', 'commercial', 'retail']);
/** The zoom the land use is read at, and how far round a town it is looked for, tiles each way. */
const SITE_ZOOM = 13;
const SITE_SPAN = 1;

async function tile(template: string, z: number, x: number, y: number, signal?: AbortSignal): Promise<VectorTile | undefined> {
  const url = template.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
  const r = await fetch(url, { mode: 'cors', signal });
  if (!r.ok) return undefined;
  return new VectorTile(new PbfReader(new Uint8Array(await r.arrayBuffer())));
}

/** The tiles at zoom `z` within `radius` metres of (lat, lon), at most `cap` each way. */
function around(lat: number, lon: number, radius: number, z: number, cap: number): Array<[number, number]> {
  const dLat = radius / 111_320, dLon = radius / (111_320 * Math.cos((lat * Math.PI) / 180));
  const cx = Math.floor(lonToTileXf(lon, z)), cy = Math.floor(latToTileYf(lat, z));
  const x0 = Math.max(cx - cap, Math.floor(lonToTileXf(lon - dLon, z))), x1 = Math.min(cx + cap, Math.floor(lonToTileXf(lon + dLon, z)));
  const y0 = Math.max(cy - cap, Math.floor(latToTileYf(lat + dLat, z))), y1 = Math.min(cy + cap, Math.floor(latToTileYf(lat - dLat, z)));
  const out: Array<[number, number]> = [];
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) out.push([x, y]);
  return out;
}

async function placesNear(template: string, lat: number, lon: number, radius: number, signal?: AbortSignal): Promise<Place[]> {
  // Towns are in the coarse tiles; villages and suburbs only in finer ones.
  const z = radius > 12_000 ? 8 : 10;
  const tiles = await Promise.all(around(lat, lon, radius, z, 2).map(([x, y]) => tile(template, z, x, y, signal).then(t => ({ t, x, y })).catch(() => undefined)));
  const places: Place[] = [];
  const seen = new Set<string>();
  for (const got of tiles) {
    const layer = got?.t?.layers.place_labels;
    if (layer === undefined) continue;
    for (let i = 0; i < layer.length; i++) {
      const f = layer.feature(i);
      const name = f.properties.name, kind = PLACE_KIND[String(f.properties.kind)];
      const pt = f.loadGeometry()[0]?.[0];
      if (typeof name !== 'string' || kind === undefined || pt === undefined || seen.has(name)) continue;
      seen.add(name);
      places.push({ name, kind, lon: tileXToLon(got!.x + pt.x / layer.extent, z), lat: tileYToLat(got!.y + pt.y / layer.extent, z) });
    }
  }
  return places;
}

async function sitesNear(template: string, lat: number, lon: number, signal?: AbortSignal): Promise<Site[]> {
  const z = SITE_ZOOM;
  const cx = Math.floor(lonToTileXf(lon, z)), cy = Math.floor(latToTileYf(lat, z));
  const jobs: Array<Promise<Site[]>> = [];
  for (let y = cy - SITE_SPAN; y <= cy + SITE_SPAN; y++) for (let x = cx - SITE_SPAN; x <= cx + SITE_SPAN; x++) {
    jobs.push(tile(template, z, x, y, signal).then((t) => {
      const layer = t?.layers.land;
      const sites: Site[] = [];
      if (layer === undefined) return sites;
      const metresPer = (40_075_016 * Math.cos((lat * Math.PI) / 180)) / 2 ** z / layer.extent;
      for (let i = 0; i < layer.length; i++) {
        const f = layer.feature(i);
        const kind = String(f.properties.kind);
        if (!SITE_KINDS.has(kind)) continue;
        // Each outer ring an estate: its middle, and how big it is.
        for (const ring of f.loadGeometry()) {
          let a = 0, sx = 0, sy = 0;
          for (let k = 0, n = ring.length; k < n; k++) {
            const p = ring[k], q = ring[(k + 1) % n];
            const c = p.x * q.y - q.x * p.y;
            a += c; sx += (p.x + q.x) * c; sy += (p.y + q.y) * c;
          }
          const area = Math.abs(a / 2) * metresPer * metresPer;
          if (area < 8000 || a === 0) continue;
          const u = sx / (3 * a), v = sy / (3 * a);
          if (u < 0 || u >= layer.extent || v < 0 || v >= layer.extent) continue;
          sites.push({ kind, area, lon: tileXToLon(x + u / layer.extent, z), lat: tileYToLat(y + v / layer.extent, z) });
        }
      }
      return sites;
    }).catch(() => []));
  }
  return (await Promise.all(jobs)).flat();
}

/**
 * A place to deliver to near `target`, within `radius` metres of it and at
 * least `minFrom` metres from `from`: its position, and what it is called.
 * From the vector tiles at `template`.
 */
export async function findDropOff(
  template: string,
  target: { lat: number; lon: number }, radius: number,
  from: { lat: number; lon: number }, minFrom: number, signal?: AbortSignal,
): Promise<{ lat: number; lon: number; name: string } | undefined> {
  const places = await placesNear(template, target.lat, target.lon, radius, signal);
  const town = chooseDropOff(places, [], target, from, minFrom);
  if (town === undefined) return undefined;
  const sites = await sitesNear(template, town.lat, town.lon, signal);
  return chooseDropOff(places, sites, target, from, minFrom);
}
